@if (form) {
<form [formGroup]="form">
    <tec-tabs
        [type]="'default'"
        [tabs]="sectionTabs"
        [maxTabsToShow]="sectionTabs.length"
        (tabSelected)="sectionSelected($event)"
    />

    <div
        [class.layout--60-40]="selectedSection !== DesignThemeSection.FEEDBACK"
        [class.form__editor--feedback]="selectedSection === DesignThemeSection.FEEDBACK"
    >
        <div class="form">
            @switch (selectedSection) {
                @case (DesignThemeSection.GENERAL) {
                    <ng-container *ngTemplateOutlet="themeGeneral" />
                }
                @case (DesignThemeSection.TEXT) {
                    <ng-container *ngTemplateOutlet="themeText" />
                }
                @case (DesignThemeSection.BUTTONS) {
                    <ng-container *ngTemplateOutlet="themeButtons" />
                }
                @case (DesignThemeSection.LINKS) {
                    <ng-container *ngTemplateOutlet="themeLink" />
                }
                @case (DesignThemeSection.GRAPHICS) {
                    <ng-container *ngTemplateOutlet="themeIcons" />
                }
                @case (DesignThemeSection.FEEDBACK) {
                    <ng-container *ngTemplateOutlet="themeFeedback" />
                }
                @case (DesignThemeSection.FORMS) {
                    <ng-container *ngTemplateOutlet="themeLoading" />
                }
                @case (DesignThemeSection.BORDERS) {
                    <ng-container *ngTemplateOutlet="themeBorders" />
                }
                @case (DesignThemeSection.ADVANCED) {
                    <ng-container *ngTemplateOutlet="themeAdvancedConfig" />
                }
            }
        </div>

        @if (selectedSection !== DesignThemeSection.FEEDBACK) {
            <app-design-theme-preview
                [themeData]="previewThemeData"
                [themeMode]="themeMode"
                [logoSrc]="base64Logo"
                [customSpinnerSrc]="customSpinnerPreviewSrc"
                [section]="selectedSection"
            />
        }
    </div>

    <!-- GENERAL -->
    <ng-template #themeGeneral>
        <tec-tile
            [title]="'DESIGN.THEME_DETAIL.LOGO.TITLE' | translate"
            [subtitle]="'DESIGN.THEME_DETAIL.LOGO.DESCRIPTION' | translate"
        >
            @if (base64Logo) {
            <div class="form__logo-preview">
                <tec-tag-image [size]="'xl'" [img]="base64Logo" />
                <tec-link [label]="'BUTTON.DELETE' | translate" (click)="clearLogo()" />
            </div>
            } @else {
            <tec-upload
                [size]="'sm'"
                [acceptedFileTypes]="acceptedFileTypes"
                [hasButton]="true"
                formControlName="logo"
            />
            }
        </tec-tile>

        <tec-tile
            [title]="'DESIGN.THEME_DETAIL.FAVICON.TITLE' | translate"
            [subtitle]="'DESIGN.THEME_DETAIL.FAVICON.DESCRIPTION' | translate"
        >
            @if (base64Favicon) {
            <div class="form__logo-preview">
                <tec-tag-image [size]="'xl'" [img]="base64Favicon" />
                <tec-link [label]="'BUTTON.DELETE' | translate" (click)="clearFavicon()" />
            </div>
            } @else {
            <tec-upload
                [size]="'sm'"
                [acceptedFileTypes]="acceptedFileTypes"
                [hasButton]="true"
                formControlName="favicon"
            />
            }
        </tec-tile>

        <tec-tile
            [title]="'DESIGN.THEME_DETAIL.COLORS.TITLE' | translate"
            [subtitle]="'DESIGN.THEME_DETAIL.COLORS.DESCRIPTION' | translate"
        >
            <ng-container actions>
                <tec-link
                    [label]="'DESIGN.THEME_DETAIL.COLORS.RESET' | translate"
                    (click)="resetBrandColors()"
                />
            </ng-container>
            <div class="grid grid-i3">
                <tec-form-group [label]="'DESIGN.THEME_DETAIL.COLORS.PRIMARY' | translate">
                    <div class="form__color-row">
                        <tec-select type="color-picker" formControlName="brand_primary" [options]="[]" />
                        <input
                            class="form__hex-input"
                            type="text"
                            [value]="getColorHex('brand_primary')"
                            (input)="onHexInput('brand_primary', $event)"
                        />
                    </div>
                </tec-form-group>
                <tec-form-group [label]="'DESIGN.THEME_DETAIL.COLORS.SECONDARY' | translate">
                    <div class="form__color-row">
                        <tec-select type="color-picker" formControlName="brand_secondary" [options]="[]" />
                        <input
                            class="form__hex-input"
                            type="text"
                            [value]="getColorHex('brand_secondary')"
                            (input)="onHexInput('brand_secondary', $event)"
                        />
                    </div>
                </tec-form-group>
                <tec-form-group [label]="'DESIGN.THEME_DETAIL.COLORS.ACCENT' | translate">
                    <div class="form__color-row">
                        <tec-select type="color-picker" formControlName="brand_accent" [options]="[]" />
                        <input
                            class="form__hex-input"
                            type="text"
                            [value]="getColorHex('brand_accent')"
                            (input)="onHexInput('brand_accent', $event)"
                        />
                    </div>
                </tec-form-group>
                @if (themeMode?.toLowerCase() !== 'dark') {
                <tec-form-group [label]="'DESIGN.THEME_DETAIL.COLORS.EMPHASIS' | translate">
                    <div class="form__color-row">
                        <tec-select type="color-picker" formControlName="brand_emphasis" [options]="[]" />
                        <input
                            class="form__hex-input"
                            type="text"
                            [value]="getColorHex('brand_emphasis')"
                            (input)="onHexInput('brand_emphasis', $event)"
                        />
                    </div>
                </tec-form-group>
                }
                <tec-form-group [label]="'DESIGN.THEME_DETAIL.COLORS.TONE_PRIMARY' | translate">
                    <div class="form__color-row">
                        <tec-select type="color-picker" formControlName="brand_neutral" [options]="[]" />
                        <input
                            class="form__hex-input"
                            type="text"
                            [value]="getColorHex('brand_neutral')"
                            (input)="onHexInput('brand_neutral', $event)"
                        />
                    </div>
                </tec-form-group>
                <tec-form-group [label]="'DESIGN.THEME_DETAIL.COLORS.TONE_SECONDARY' | translate">
                    <div class="form__color-row">
                        <tec-select type="color-picker" formControlName="brand_tone_secondary" [options]="[]" />
                        <input
                            class="form__hex-input"
                            type="text"
                            [value]="getColorHex('brand_tone_secondary')"
                            (input)="onHexInput('brand_tone_secondary', $event)"
                        />
                    </div>
                </tec-form-group>
                <tec-form-group [label]="'DESIGN.THEME_DETAIL.COLORS.TONE_TERTIARY' | translate">
                    <div class="form__color-row">
                        <tec-select type="color-picker" formControlName="brand_tone_tertiary" [options]="[]" />
                        <input
                            class="form__hex-input"
                            type="text"
                            [value]="getColorHex('brand_tone_tertiary')"
                            (input)="onHexInput('brand_tone_tertiary', $event)"
                        />
                    </div>
                </tec-form-group>
            </div>
        </tec-tile>
    </ng-template>

    <!-- TEXT -->
    <ng-template #themeText>
        <tec-tile
            [title]="'DESIGN.THEME_DETAIL.TEXT.TYPOGRAPHY_TITLE' | translate"
            [subtitle]="'DESIGN.THEME_DETAIL.TEXT.TYPOGRAPHY_DESCRIPTION' | translate"
        >
            <ng-container actions>
                <tec-link
                    [label]="'DESIGN.THEME_DETAIL.TEXT.RESET' | translate"
                    (click)="resetTextTypography()"
                />
            </ng-container>
            <tec-select
                [placeholder]="'DESIGN.THEME_DETAIL.TEXT.SELECT_PLACEHOLDER' | translate"
                formControlName="text_typography"
                [options]="designHelper.typographyOptions"
            />
        </tec-tile>

        <tec-tile
            [title]="'DESIGN.THEME_DETAIL.TEXT.COLOR_TITLE' | translate"
            [subtitle]="'DESIGN.THEME_DETAIL.TEXT.COLOR_DESCRIPTION' | translate"
        >
            <ng-container actions>
                <tec-link
                    [label]="'DESIGN.THEME_DETAIL.TEXT.RESET' | translate"
                    (click)="resetTextColors()"
                />
            </ng-container>
            <div class="grid grid-i2">
                <tec-form-group [label]="'DESIGN.THEME_DETAIL.TEXT.COLOR_TITLE_LABEL' | translate">
                    <div class="form__color-row">
                        <tec-select type="color-picker" formControlName="text_color_title" [options]="[]" />
                        <input
                            class="form__hex-input"
                            type="text"
                            [value]="getColorHex('text_color_title')"
                            (input)="onHexInput('text_color_title', $event)"
                        />
                    </div>
                </tec-form-group>
                <tec-form-group [label]="'DESIGN.THEME_DETAIL.TEXT.COLOR_PRIMARY' | translate">
                    <div class="form__color-row">
                        <tec-select type="color-picker" formControlName="text_color_primary" [options]="[]" />
                        <input
                            class="form__hex-input"
                            type="text"
                            [value]="getColorHex('text_color_primary')"
                            (input)="onHexInput('text_color_primary', $event)"
                        />
                    </div>
                </tec-form-group>
                <tec-form-group [label]="'DESIGN.THEME_DETAIL.TEXT.COLOR_SECONDARY' | translate">
                    <div class="form__color-row">
                        <tec-select type="color-picker" formControlName="text_color_secondary" [options]="[]" />
                        <input
                            class="form__hex-input"
                            type="text"
                            [value]="getColorHex('text_color_secondary')"
                            (input)="onHexInput('text_color_secondary', $event)"
                        />
                    </div>
                </tec-form-group>
                <tec-form-group [label]="'DESIGN.THEME_DETAIL.TEXT.COLOR_TERTIARY' | translate">
                    <div class="form__color-row">
                        <tec-select type="color-picker" formControlName="text_color_tertiary" [options]="[]" />
                        <input
                            class="form__hex-input"
                            type="text"
                            [value]="getColorHex('text_color_tertiary')"
                            (input)="onHexInput('text_color_tertiary', $event)"
                        />
                    </div>
                </tec-form-group>
            </div>
        </tec-tile>
    </ng-template>

    <!-- BUTTONS -->
    <ng-template #themeButtons>
        <tec-tile
            [title]="'DESIGN.THEME_DETAIL.BUTTONS.RADIUS_TITLE' | translate"
            [subtitle]="'DESIGN.THEME_DETAIL.BUTTONS.RADIUS_DESCRIPTION' | translate"
        >
            <ng-container actions>
                <tec-link
                    [label]="'DESIGN.THEME_DETAIL.BUTTONS.RESET' | translate"
                    (click)="resetButtonRadius()"
                />
            </ng-container>
            <div class="grid grid-i2">
                <tec-form-group [label]="'DESIGN.THEME_DETAIL.BUTTONS.RADIUS_PRIMARY_SECONDARY' | translate">
                    <tec-input-quantity
                        [min]="0"
                        [step]="1"
                        formControlName="button_border_round"
                    ></tec-input-quantity>
                </tec-form-group>
                <tec-form-group [label]="'DESIGN.THEME_DETAIL.BUTTONS.RADIUS_ICON' | translate">
                    <tec-input-quantity
                        [min]="0"
                        [step]="1"
                        formControlName="button_icon_border_round"
                    ></tec-input-quantity>
                </tec-form-group>
            </div>
        </tec-tile>

        <tec-tile
            [title]="'DESIGN.THEME_DETAIL.BUTTONS.PRIMARY_TITLE' | translate"
            [subtitle]="'DESIGN.THEME_DETAIL.BUTTONS.PRIMARY_DESCRIPTION' | translate"
        >
            <ng-container actions>
                <tec-link
                    [label]="'DESIGN.THEME_DETAIL.BUTTONS.RESET' | translate"
                    (click)="resetButtonPrimaryColors()"
                />
            </ng-container>
            <div class="grid grid-i3">
                <tec-form-group [label]="'DESIGN.THEME_DETAIL.BUTTONS.BACKGROUND' | translate">
                    <div class="form__color-row">
                        <tec-select type="color-picker" formControlName="button_primary_background_color" [options]="[]" />
                        <input
                            class="form__hex-input"
                            type="text"
                            [value]="getColorHex('button_primary_background_color')"
                            (input)="onHexInput('button_primary_background_color', $event)"
                        />
                    </div>
                </tec-form-group>
                <tec-form-group [label]="'DESIGN.THEME_DETAIL.BUTTONS.TEXT' | translate">
                    <div class="form__color-row">
                        <tec-select type="color-picker" formControlName="button_primary_text_color" [options]="[]" />
                        <input
                            class="form__hex-input"
                            type="text"
                            [value]="getColorHex('button_primary_text_color')"
                            (input)="onHexInput('button_primary_text_color', $event)"
                        />
                    </div>
                </tec-form-group>
                <tec-form-group [label]="'DESIGN.THEME_DETAIL.BUTTONS.BORDER' | translate">
                    <div class="form__color-row">
                        <tec-select type="color-picker" formControlName="button_primary_border_color" [options]="[]" />
                        <input
                            class="form__hex-input"
                            type="text"
                            [value]="getColorHex('button_primary_border_color')"
                            (input)="onHexInput('button_primary_border_color', $event)"
                        />
                    </div>
                </tec-form-group>
                <tec-form-group [label]="'DESIGN.THEME_DETAIL.BUTTONS.BACKGROUND_HOVER' | translate">
                    <div class="form__color-row">
                        <tec-select type="color-picker" formControlName="button_primary_background_color_hover" [options]="[]" />
                        <input
                            class="form__hex-input"
                            type="text"
                            [value]="getColorHex('button_primary_background_color_hover')"
                            (input)="onHexInput('button_primary_background_color_hover', $event)"
                        />
                    </div>
                </tec-form-group>
                <tec-form-group [label]="'DESIGN.THEME_DETAIL.BUTTONS.TEXT_HOVER' | translate">
                    <div class="form__color-row">
                        <tec-select type="color-picker" formControlName="button_primary_text_color_hover" [options]="[]" />
                        <input
                            class="form__hex-input"
                            type="text"
                            [value]="getColorHex('button_primary_text_color_hover')"
                            (input)="onHexInput('button_primary_text_color_hover', $event)"
                        />
                    </div>
                </tec-form-group>
                <tec-form-group [label]="'DESIGN.THEME_DETAIL.BUTTONS.BORDER_HOVER' | translate">
                    <div class="form__color-row">
                        <tec-select type="color-picker" formControlName="button_primary_border_color_hover" [options]="[]" />
                        <input
                            class="form__hex-input"
                            type="text"
                            [value]="getColorHex('button_primary_border_color_hover')"
                            (input)="onHexInput('button_primary_border_color_hover', $event)"
                        />
                    </div>
                </tec-form-group>
            </div>
        </tec-tile>

        <tec-tile
            [title]="'DESIGN.THEME_DETAIL.BUTTONS.SECONDARY_TITLE' | translate"
            [subtitle]="'DESIGN.THEME_DETAIL.BUTTONS.SECONDARY_DESCRIPTION' | translate"
        >
            <ng-container actions>
                <tec-link
                    [label]="'DESIGN.THEME_DETAIL.BUTTONS.RESET' | translate"
                    (click)="resetButtonSecondaryColors()"
                />
            </ng-container>
            <div class="grid grid-i3">
                <tec-form-group [label]="'DESIGN.THEME_DETAIL.BUTTONS.BACKGROUND' | translate">
                    <div class="form__color-row">
                        <tec-select type="color-picker" formControlName="button_secondary_background_color" [options]="[]" />
                        <input
                            class="form__hex-input"
                            type="text"
                            [value]="getColorHex('button_secondary_background_color')"
                            (input)="onHexInput('button_secondary_background_color', $event)"
                        />
                    </div>
                </tec-form-group>
                <tec-form-group [label]="'DESIGN.THEME_DETAIL.BUTTONS.TEXT' | translate">
                    <div class="form__color-row">
                        <tec-select type="color-picker" formControlName="button_secondary_text_color" [options]="[]" />
                        <input
                            class="form__hex-input"
                            type="text"
                            [value]="getColorHex('button_secondary_text_color')"
                            (input)="onHexInput('button_secondary_text_color', $event)"
                        />
                    </div>
                </tec-form-group>
                <tec-form-group [label]="'DESIGN.THEME_DETAIL.BUTTONS.BORDER' | translate">
                    <div class="form__color-row">
                        <tec-select type="color-picker" formControlName="button_secondary_border_color" [options]="[]" />
                        <input
                            class="form__hex-input"
                            type="text"
                            [value]="getColorHex('button_secondary_border_color')"
                            (input)="onHexInput('button_secondary_border_color', $event)"
                        />
                    </div>
                </tec-form-group>
                <tec-form-group [label]="'DESIGN.THEME_DETAIL.BUTTONS.BACKGROUND_HOVER' | translate">
                    <div class="form__color-row">
                        <tec-select type="color-picker" formControlName="button_secondary_background_color_hover" [options]="[]" />
                        <input
                            class="form__hex-input"
                            type="text"
                            [value]="getColorHex('button_secondary_background_color_hover')"
                            (input)="onHexInput('button_secondary_background_color_hover', $event)"
                        />
                    </div>
                </tec-form-group>
                <tec-form-group [label]="'DESIGN.THEME_DETAIL.BUTTONS.TEXT_HOVER' | translate">
                    <div class="form__color-row">
                        <tec-select type="color-picker" formControlName="button_secondary_text_color_hover" [options]="[]" />
                        <input
                            class="form__hex-input"
                            type="text"
                            [value]="getColorHex('button_secondary_text_color_hover')"
                            (input)="onHexInput('button_secondary_text_color_hover', $event)"
                        />
                    </div>
                </tec-form-group>
                <tec-form-group [label]="'DESIGN.THEME_DETAIL.BUTTONS.BORDER_HOVER' | translate">
                    <div class="form__color-row">
                        <tec-select type="color-picker" formControlName="button_secondary_border_color_hover" [options]="[]" />
                        <input
                            class="form__hex-input"
                            type="text"
                            [value]="getColorHex('button_secondary_border_color_hover')"
                            (input)="onHexInput('button_secondary_border_color_hover', $event)"
                        />
                    </div>
                </tec-form-group>
            </div>
        </tec-tile>

        <tec-tile
            [title]="'DESIGN.THEME_DETAIL.BUTTONS.ICON_TITLE' | translate"
            [subtitle]="'DESIGN.THEME_DETAIL.BUTTONS.ICON_DESCRIPTION' | translate"
        >
            <ng-container actions>
                <tec-link
                    [label]="'DESIGN.THEME_DETAIL.BUTTONS.RESET' | translate"
                    (click)="resetButtonIconColors()"
                />
            </ng-container>
            <div class="grid grid-i2">
                <tec-form-group [label]="'DESIGN.THEME_DETAIL.BUTTONS.ICON' | translate">
                    <div class="form__color-row">
                        <tec-select type="color-picker" formControlName="button_icon_color" [options]="[]" />
                        <input
                            class="form__hex-input"
                            type="text"
                            [value]="getColorHex('button_icon_color')"
                            (input)="onHexInput('button_icon_color', $event)"
                        />
                    </div>
                </tec-form-group>
                <tec-form-group [label]="'DESIGN.THEME_DETAIL.BUTTONS.BACKGROUND' | translate">
                    <div class="form__color-row">
                        <tec-select type="color-picker" formControlName="button_icon_background_color" [options]="[]" />
                        <input
                            class="form__hex-input"
                            type="text"
                            [value]="getColorHex('button_icon_background_color')"
                            (input)="onHexInput('button_icon_background_color', $event)"
                        />
                    </div>
                </tec-form-group>
                <tec-form-group [label]="'DESIGN.THEME_DETAIL.BUTTONS.ICON_HOVER' | translate">
                    <div class="form__color-row">
                        <tec-select type="color-picker" formControlName="button_icon_color_hover" [options]="[]" />
                        <input
                            class="form__hex-input"
                            type="text"
                            [value]="getColorHex('button_icon_color_hover')"
                            (input)="onHexInput('button_icon_color_hover', $event)"
                        />
                    </div>
                </tec-form-group>
                <tec-form-group [label]="'DESIGN.THEME_DETAIL.BUTTONS.BACKGROUND_HOVER' | translate">
                    <div class="form__color-row">
                        <tec-select type="color-picker" formControlName="button_icon_background_color_hover" [options]="[]" />
                        <input
                            class="form__hex-input"
                            type="text"
                            [value]="getColorHex('button_icon_background_color_hover')"
                            (input)="onHexInput('button_icon_background_color_hover', $event)"
                        />
                    </div>
                </tec-form-group>
                <tec-form-group [label]="'DESIGN.THEME_DETAIL.BUTTONS.ICON_ACTIVE' | translate">
                    <div class="form__color-row">
                        <tec-select type="color-picker" formControlName="button_icon_color_active" [options]="[]" />
                        <input
                            class="form__hex-input"
                            type="text"
                            [value]="getColorHex('button_icon_color_active')"
                            (input)="onHexInput('button_icon_color_active', $event)"
                        />
                    </div>
                </tec-form-group>
                <tec-form-group [label]="'DESIGN.THEME_DETAIL.BUTTONS.BACKGROUND_ACTIVE' | translate">
                    <div class="form__color-row">
                        <tec-select type="color-picker" formControlName="button_icon_background_color_active" [options]="[]" />
                        <input
                            class="form__hex-input"
                            type="text"
                            [value]="getColorHex('button_icon_background_color_active')"
                            (input)="onHexInput('button_icon_background_color_active', $event)"
                        />
                    </div>
                </tec-form-group>
            </div>
        </tec-tile>
    </ng-template>

    <!-- LINK -->
    <ng-template #themeLink>
        <tec-tile
            [title]="'DESIGN.THEME_DETAIL.LINK.TITLE' | translate"
            [subtitle]="'DESIGN.THEME_DETAIL.LINK.DESCRIPTION' | translate"
        >
            <ng-container actions>
                <tec-link
                    [label]="'DESIGN.THEME_DETAIL.LINK.RESET' | translate"
                    (click)="resetLinkStyle()"
                />
            </ng-container>
            <div class="grid grid-i2">
                <tec-form-group [label]="'DESIGN.THEME_DETAIL.LINK.COLOR_REST' | translate">
                    <div class="form__color-row">
                        <tec-select type="color-picker" formControlName="link_color" [options]="[]" />
                        <input
                            class="form__hex-input"
                            type="text"
                            [value]="getColorHex('link_color')"
                            (input)="onHexInput('link_color', $event)"
                        />
                    </div>
                </tec-form-group>
                <tec-form-group [label]="'DESIGN.THEME_DETAIL.LINK.UNDERLINE_REST' | translate">
                    <tec-switch formControlName="links_underline" />
                </tec-form-group>
                <tec-form-group [label]="'DESIGN.THEME_DETAIL.LINK.COLOR_HOVER' | translate">
                    <div class="form__color-row">
                        <tec-select type="color-picker" formControlName="link_color_hover" [options]="[]" />
                        <input
                            class="form__hex-input"
                            type="text"
                            [value]="getColorHex('link_color_hover')"
                            (input)="onHexInput('link_color_hover', $event)"
                        />
                    </div>
                </tec-form-group>
                <tec-form-group [label]="'DESIGN.THEME_DETAIL.LINK.UNDERLINE_HOVER' | translate">
                    <tec-switch formControlName="links_underline_hover" />
                </tec-form-group>
            </div>
        </tec-tile>
    </ng-template>

    <ng-template #themeIcons>
        <tec-tile
            [title]="'DESIGN.THEME_DETAIL.GRAPHICS.TITLE' | translate"
            [subtitle]="'DESIGN.THEME_DETAIL.GRAPHICS.DESCRIPTION' | translate"
        >
            <ng-container actions>
                <tec-link
                    [label]="'DESIGN.THEME_DETAIL.GRAPHICS.RESET' | translate"
                    (click)="resetGraphicsColors()"
                />
            </ng-container>
            <div class="grid grid-i2">
                <tec-form-group [label]="'DESIGN.THEME_DETAIL.GRAPHICS.PRIMARY_BORDER' | translate">
                    <div class="form__color-row">
                        <tec-select type="color-picker" formControlName="icon_border_color" [options]="[]" />
                        <input
                            class="form__hex-input"
                            type="text"
                            [value]="getColorHex('icon_border_color')"
                            (input)="onHexInput('icon_border_color', $event)"
                        />
                    </div>
                </tec-form-group>
                <tec-form-group [label]="'DESIGN.THEME_DETAIL.GRAPHICS.SECONDARY_BORDER' | translate">
                    <div class="form__color-row">
                        <tec-select type="color-picker" formControlName="icon_secondary_border_color" [options]="[]" />
                        <input
                            class="form__hex-input"
                            type="text"
                            [value]="getColorHex('icon_secondary_border_color')"
                            (input)="onHexInput('icon_secondary_border_color', $event)"
                        />
                    </div>
                </tec-form-group>
                <tec-form-group [label]="'DESIGN.THEME_DETAIL.GRAPHICS.PRIMARY_BACKGROUND' | translate">
                    <div class="form__color-row">
                        <tec-select type="color-picker" formControlName="icon_background_color" [options]="[]" />
                        <input
                            class="form__hex-input"
                            type="text"
                            [value]="getColorHex('icon_background_color')"
                            (input)="onHexInput('icon_background_color', $event)"
                        />
                    </div>
                </tec-form-group>
                <tec-form-group [label]="'DESIGN.THEME_DETAIL.GRAPHICS.SECONDARY_BACKGROUND' | translate">
                    <div class="form__color-row">
                        <tec-select type="color-picker" formControlName="icon_secondary_background_color" [options]="[]" />
                        <input
                            class="form__hex-input"
                            type="text"
                            [value]="getColorHex('icon_secondary_background_color')"
                            (input)="onHexInput('icon_secondary_background_color', $event)"
                        />
                    </div>
                </tec-form-group>
                <tec-form-group [label]="'DESIGN.THEME_DETAIL.GRAPHICS.PRIMARY_ICON' | translate">
                    <div class="form__color-row">
                        <tec-select type="color-picker" formControlName="icon_primary_color" [options]="[]" />
                        <input
                            class="form__hex-input"
                            type="text"
                            [value]="getColorHex('icon_primary_color')"
                            (input)="onHexInput('icon_primary_color', $event)"
                        />
                    </div>
                </tec-form-group>
                <tec-form-group [label]="'DESIGN.THEME_DETAIL.GRAPHICS.SECONDARY_ICON' | translate">
                    <div class="form__color-row">
                        <tec-select type="color-picker" formControlName="icon_secondary_color" [options]="[]" />
                        <input
                            class="form__hex-input"
                            type="text"
                            [value]="getColorHex('icon_secondary_color')"
                            (input)="onHexInput('icon_secondary_color', $event)"
                        />
                    </div>
                </tec-form-group>
            </div>
        </tec-tile>
    </ng-template>

    <!-- CARGA (FORMS) -->
    <ng-template #themeLoading>
        <tec-tile
            [title]="'DESIGN.THEME_DETAIL.LOADING.PROGRESS_TITLE' | translate"
            [subtitle]="'DESIGN.THEME_DETAIL.LOADING.PROGRESS_DESCRIPTION' | translate"
        >
            <ng-container actions>
                <tec-link
                    [label]="'DESIGN.THEME_DETAIL.LOADING.RESET' | translate"
                    (click)="resetLoadingProgress()"
                />
            </ng-container>
            <div class="grid grid-i2">
                <tec-form-group [label]="'DESIGN.THEME_DETAIL.LOADING.COLOR' | translate">
                    <div class="form__color-row">
                        <tec-select type="color-picker" formControlName="progress_fill_color" [options]="[]" />
                        <input
                            class="form__hex-input"
                            type="text"
                            [value]="getColorHex('progress_fill_color')"
                            (input)="onHexInput('progress_fill_color', $event)"
                        />
                    </div>
                </tec-form-group>
                <tec-form-group [label]="'DESIGN.THEME_DETAIL.LOADING.PROGRESS_RADIUS' | translate">
                    <tec-input-quantity
                        [min]="0"
                        [step]="1"
                        formControlName="progress_border_round"
                    ></tec-input-quantity>
                </tec-form-group>
            </div>
            <tec-divider />
            <tec-form-group
                [label]="'DESIGN.THEME_DETAIL.LOADING.CUSTOM_SPINNER' | translate"
                [description]="'DESIGN.THEME_DETAIL.LOADING.CUSTOM_SPINNER_DESCRIPTION' | translate"
            >
                @if (customSpinnerPreviewSrc) {
                <div class="form__logo-preview">
                    <tec-tag-image [size]="'xl'" [img]="customSpinnerPreviewSrc" />
                    <tec-link [label]="'BUTTON.DELETE' | translate" (click)="changeCustomSpinner()" />
                </div>
                } @else {
                <tec-upload
                    [size]="'sm'"
                    [acceptedFileTypes]="customSpinnerAcceptedFileTypes"
                    [hasButton]="true"
                    formControlName="custom_spinner"
                />
                }
            </tec-form-group>
        </tec-tile>

        <tec-tile
            [title]="'DESIGN.THEME_DETAIL.LOADING.CAPTURE_GUIDE_TITLE' | translate"
            [subtitle]="'DESIGN.THEME_DETAIL.LOADING.CAPTURE_GUIDE_DESCRIPTION' | translate"
        >
            <ng-container actions>
                <tec-link
                    [label]="'DESIGN.THEME_DETAIL.LOADING.RESET' | translate"
                    (click)="resetCaptureGuideColor()"
                />
            </ng-container>
            <div class="grid grid-i2">
                <div class="form__color-row">
                    <tec-select type="color-picker" formControlName="capture_guide_color" [options]="[]" />
                    <input
                        class="form__hex-input"
                        type="text"
                        [value]="getColorHex('capture_guide_color')"
                        (input)="onHexInput('capture_guide_color', $event)"
                    />
                </div>
            </div>
        </tec-tile>
    </ng-template>

    <ng-template #themeFeedback>
        <div
            class="grid grid-i2 form__feedback-grid"
            [class.form__feedback-grid--dark]="isDarkSkin"
        >
            <tec-tile
                [title]="'DESIGN.THEME_DETAIL.FEEDBACK.INFO_TITLE' | translate"
                [subtitle]="'DESIGN.THEME_DETAIL.FEEDBACK.INFO_DESCRIPTION' | translate"
            >
                <ng-container actions>
                    <tec-link
                        [label]="'DESIGN.THEME_DETAIL.FEEDBACK.RESET' | translate"
                        (click)="resetFeedbackInfoColors()"
                    />
                </ng-container>
                <div class="grid grid-i2">
                    <tec-form-group [label]="'DESIGN.THEME_DETAIL.FEEDBACK.BACKGROUND' | translate">
                        <div class="form__color-row">
                            <tec-select type="color-picker" formControlName="feedback_info_surface" [options]="[]" />
                            <input
                                class="form__hex-input"
                                type="text"
                                [value]="getColorHex('feedback_info_surface')"
                                (input)="onHexInput('feedback_info_surface', $event)"
                            />
                        </div>
                    </tec-form-group>
                    <tec-form-group [label]="'DESIGN.THEME_DETAIL.FEEDBACK.ICON' | translate">
                        <div class="form__color-row">
                            <tec-select type="color-picker" formControlName="feedback_on_info_surface" [options]="[]" />
                            <input
                                class="form__hex-input"
                                type="text"
                                [value]="getColorHex('feedback_on_info_surface')"
                                (input)="onHexInput('feedback_on_info_surface', $event)"
                            />
                        </div>
                    </tec-form-group>
                </div>
                <div
                    class="form__feedback-preview form__feedback-preview--info"
                    [style.--feedback-surface]="getColorHex('feedback_info_surface')"
                    [style.--feedback-on-surface]="getColorHex('feedback_on_info_surface')"
                    [style.--text-primary]="getColorHex('text_color_title')"
                >
                    <tec-tile
                        type="status"
                        tagIconStatus="info"
                        icon="icon-award"
                        [title]="'DESIGN.THEME_DETAIL.FEEDBACK.INFO_PREVIEW' | translate"
                    />
                </div>
            </tec-tile>

            <tec-tile
                [title]="'DESIGN.THEME_DETAIL.FEEDBACK.ALERT_TITLE' | translate"
                [subtitle]="'DESIGN.THEME_DETAIL.FEEDBACK.ALERT_DESCRIPTION' | translate"
            >
                <ng-container actions>
                    <tec-link
                        [label]="'DESIGN.THEME_DETAIL.FEEDBACK.RESET' | translate"
                        (click)="resetFeedbackAlertColors()"
                    />
                </ng-container>
                <div class="grid grid-i2">
                    <tec-form-group [label]="'DESIGN.THEME_DETAIL.FEEDBACK.BACKGROUND' | translate">
                        <div class="form__color-row">
                            <tec-select type="color-picker" formControlName="feedback_warning_surface" [options]="[]" />
                            <input
                                class="form__hex-input"
                                type="text"
                                [value]="getColorHex('feedback_warning_surface')"
                                (input)="onHexInput('feedback_warning_surface', $event)"
                            />
                        </div>
                    </tec-form-group>
                    <tec-form-group [label]="'DESIGN.THEME_DETAIL.FEEDBACK.ICON' | translate">
                        <div class="form__color-row">
                            <tec-select
                                type="color-picker"
                                formControlName="feedback_on_warning_surface"
                                [options]="[]"
                            />
                            <input
                                class="form__hex-input"
                                type="text"
                                [value]="getColorHex('feedback_on_warning_surface')"
                                (input)="onHexInput('feedback_on_warning_surface', $event)"
                            />
                        </div>
                    </tec-form-group>
                </div>
                <div
                    class="form__feedback-preview form__feedback-preview--warning"
                    [style.--feedback-surface]="getColorHex('feedback_warning_surface')"
                    [style.--feedback-on-surface]="getColorHex('feedback_on_warning_surface')"
                    [style.--text-primary]="getColorHex('text_color_title')"
                >
                    <tec-tile
                        type="status"
                        tagIconStatus="default"
                        icon="icon-alert"
                        [title]="'DESIGN.THEME_DETAIL.FEEDBACK.ALERT_PREVIEW' | translate"
                    />
                </div>
            </tec-tile>

            <tec-tile
                [title]="'DESIGN.THEME_DETAIL.FEEDBACK.SUCCESS_TITLE' | translate"
                [subtitle]="'DESIGN.THEME_DETAIL.FEEDBACK.SUCCESS_DESCRIPTION' | translate"
            >
                <ng-container actions>
                    <tec-link
                        [label]="'DESIGN.THEME_DETAIL.FEEDBACK.RESET' | translate"
                        (click)="resetFeedbackSuccessColors()"
                    />
                </ng-container>
                <div class="grid grid-i2">
                    <tec-form-group [label]="'DESIGN.THEME_DETAIL.FEEDBACK.BACKGROUND' | translate">
                        <div class="form__color-row">
                            <tec-select type="color-picker" formControlName="feedback_success_surface" [options]="[]" />
                            <input
                                class="form__hex-input"
                                type="text"
                                [value]="getColorHex('feedback_success_surface')"
                                (input)="onHexInput('feedback_success_surface', $event)"
                            />
                        </div>
                    </tec-form-group>
                    <tec-form-group [label]="'DESIGN.THEME_DETAIL.FEEDBACK.ICON' | translate">
                        <div class="form__color-row">
                            <tec-select
                                type="color-picker"
                                formControlName="feedback_on_success_surface"
                                [options]="[]"
                            />
                            <input
                                class="form__hex-input"
                                type="text"
                                [value]="getColorHex('feedback_on_success_surface')"
                                (input)="onHexInput('feedback_on_success_surface', $event)"
                            />
                        </div>
                    </tec-form-group>
                </div>
                <div
                    class="form__feedback-preview form__feedback-preview--success"
                    [style.--feedback-surface]="getColorHex('feedback_success_surface')"
                    [style.--feedback-on-surface]="getColorHex('feedback_on_success_surface')"
                    [style.--text-primary]="getColorHex('text_color_title')"
                >
                    <tec-tile
                        type="status"
                        tagIconStatus="success"
                        icon="icon-check"
                        [title]="'DESIGN.THEME_DETAIL.FEEDBACK.SUCCESS_PREVIEW' | translate"
                    />
                </div>
            </tec-tile>

            <tec-tile
                [title]="'DESIGN.THEME_DETAIL.FEEDBACK.ERROR_TITLE' | translate"
                [subtitle]="'DESIGN.THEME_DETAIL.FEEDBACK.ERROR_DESCRIPTION' | translate"
            >
                <ng-container actions>
                    <tec-link
                        [label]="'DESIGN.THEME_DETAIL.FEEDBACK.RESET' | translate"
                        (click)="resetFeedbackErrorColors()"
                    />
                </ng-container>
                <div class="grid grid-i2">
                    <tec-form-group [label]="'DESIGN.THEME_DETAIL.FEEDBACK.BACKGROUND' | translate">
                        <div class="form__color-row">
                            <tec-select type="color-picker" formControlName="feedback_error_surface" [options]="[]" />
                            <input
                                class="form__hex-input"
                                type="text"
                                [value]="getColorHex('feedback_error_surface')"
                                (input)="onHexInput('feedback_error_surface', $event)"
                            />
                        </div>
                    </tec-form-group>
                    <tec-form-group [label]="'DESIGN.THEME_DETAIL.FEEDBACK.ICON' | translate">
                        <div class="form__color-row">
                            <tec-select type="color-picker" formControlName="feedback_on_error_surface" [options]="[]" />
                            <input
                                class="form__hex-input"
                                type="text"
                                [value]="getColorHex('feedback_on_error_surface')"
                                (input)="onHexInput('feedback_on_error_surface', $event)"
                            />
                        </div>
                    </tec-form-group>
                </div>
                <div
                    class="form__feedback-preview form__feedback-preview--error"
                    [style.--feedback-surface]="getColorHex('feedback_error_surface')"
                    [style.--feedback-on-surface]="getColorHex('feedback_on_error_surface')"
                    [style.--text-primary]="getColorHex('text_color_title')"
                >
                    <tec-tile
                        type="status"
                        tagIconStatus="error"
                        icon="icon-alert"
                        [title]="'DESIGN.THEME_DETAIL.FEEDBACK.ERROR_PREVIEW' | translate"
                    />
                </div>
            </tec-tile>
        </div>
    </ng-template>

    <ng-template #themeBorders>
        <tec-tile
            [title]="'DESIGN.THEME_DETAIL.FIELDS.INPUTS_TITLE' | translate"
            [subtitle]="'DESIGN.THEME_DETAIL.FIELDS.INPUTS_DESCRIPTION' | translate"
        >
            <ng-container actions>
                <tec-link
                    [label]="'DESIGN.THEME_DETAIL.FIELDS.RESET' | translate"
                    (click)="resetFieldInputColors()"
                />
            </ng-container>
            <div class="grid grid-i2">
                <tec-form-group [label]="'DESIGN.THEME_DETAIL.FIELDS.BORDER_REST' | translate">
                    <div class="form__color-row">
                        <tec-select type="color-picker" formControlName="input_border_color" [options]="[]" />
                        <input
                            class="form__hex-input"
                            type="text"
                            [value]="getColorHex('input_border_color')"
                            (input)="onHexInput('input_border_color', $event)"
                        />
                    </div>
                </tec-form-group>
                <tec-form-group [label]="'DESIGN.THEME_DETAIL.FIELDS.BORDER_FOCUS' | translate">
                    <div class="form__color-row">
                        <tec-select type="color-picker" formControlName="input_border_color_focus" [options]="[]" />
                        <input
                            class="form__hex-input"
                            type="text"
                            [value]="getColorHex('input_border_color_focus')"
                            (input)="onHexInput('input_border_color_focus', $event)"
                        />
                    </div>
                </tec-form-group>
                <tec-form-group [label]="'DESIGN.THEME_DETAIL.FIELDS.SELECTION_TEXT' | translate">
                    <div class="form__color-row">
                        <tec-select type="color-picker" formControlName="input_selection_color" [options]="[]" />
                        <input
                            class="form__hex-input"
                            type="text"
                            [value]="getColorHex('input_selection_color')"
                            (input)="onHexInput('input_selection_color', $event)"
                        />
                    </div>
                </tec-form-group>
            </div>
        </tec-tile>

        <tec-tile
            [title]="'DESIGN.THEME_DETAIL.FIELDS.TOGGLES_TITLE' | translate"
            [subtitle]="'DESIGN.THEME_DETAIL.FIELDS.TOGGLES_DESCRIPTION' | translate"
        >
            <ng-container actions>
                <tec-link
                    [label]="'DESIGN.THEME_DETAIL.FIELDS.RESET' | translate"
                    (click)="resetFieldToggleColors()"
                />
            </ng-container>
            <div class="grid grid-i2">
                <tec-form-group [label]="'DESIGN.THEME_DETAIL.FIELDS.TOGGLE_ACTIVE' | translate">
                    <div class="form__color-row">
                        <tec-select type="color-picker" formControlName="toggle_background_color_active" [options]="[]" />
                        <input
                            class="form__hex-input"
                            type="text"
                            [value]="getColorHex('toggle_background_color_active')"
                            (input)="onHexInput('toggle_background_color_active', $event)"
                        />
                    </div>
                </tec-form-group>
                <tec-form-group [label]="'DESIGN.THEME_DETAIL.FIELDS.TOGGLE_ACTIVE_DISABLED' | translate">
                    <div class="form__color-row">
                        <tec-select
                            type="color-picker"
                            formControlName="toggle_background_color_active_disabled"
                            [options]="[]"
                        />
                        <input
                            class="form__hex-input"
                            type="text"
                            [value]="getColorHex('toggle_background_color_active_disabled')"
                            (input)="onHexInput('toggle_background_color_active_disabled', $event)"
                        />
                    </div>
                </tec-form-group>
            </div>
        </tec-tile>

        <tec-tile
            [title]="'DESIGN.THEME_DETAIL.FIELDS.RADIUS_TITLE' | translate"
            [subtitle]="'DESIGN.THEME_DETAIL.FIELDS.RADIUS_DESCRIPTION' | translate"
        >
            <ng-container actions>
                <tec-link
                    [label]="'DESIGN.THEME_DETAIL.FIELDS.RESET' | translate"
                    (click)="resetFieldInputRadius()"
                />
            </ng-container>
            <tec-input-quantity
                [min]="0"
                [step]="1"
                formControlName="input_border_round"
            ></tec-input-quantity>
        </tec-tile>
    </ng-template>

    <!-- ADVANCED CONFIG -->
    <ng-template #themeAdvancedConfig>
        <tec-tile
            [title]="'DESIGN.THEME_DETAIL.ADVANCED.RADIUS_TITLE' | translate"
            [subtitle]="'DESIGN.THEME_DETAIL.ADVANCED.RADIUS_DESCRIPTION' | translate"
        >
            <ng-container actions>
                <tec-link
                    [label]="'DESIGN.THEME_DETAIL.ADVANCED.RESET' | translate"
                    (click)="resetAdvancedRadius()"
                />
            </ng-container>
            <div class="grid grid-i2">
                <tec-form-group [label]="'DESIGN.THEME_DETAIL.ADVANCED.RADIUS_PRIMARY' | translate">
                    <tec-input-quantity
                        [min]="0"
                        [step]="1"
                        formControlName="border_round"
                    ></tec-input-quantity>
                </tec-form-group>
                <tec-form-group [label]="'DESIGN.THEME_DETAIL.ADVANCED.RADIUS_SECONDARY' | translate">
                    <tec-input-quantity
                        [min]="0"
                        [step]="1"
                        formControlName="border_round_secondary"
                    ></tec-input-quantity>
                </tec-form-group>
                <tec-form-group [label]="'DESIGN.THEME_DETAIL.ADVANCED.RADIUS_TAG' | translate">
                    <tec-input-quantity
                        [min]="0"
                        [step]="1"
                        formControlName="tag_border_round"
                    ></tec-input-quantity>
                </tec-form-group>
                <tec-form-group [label]="'DESIGN.THEME_DETAIL.ADVANCED.RADIUS_CHECKBOX' | translate">
                    <tec-input-quantity
                        [min]="0"
                        [step]="1"
                        formControlName="checkbox_border_round"
                    ></tec-input-quantity>
                </tec-form-group>
            </div>
        </tec-tile>

        <tec-tile
            [title]="'DESIGN.THEME_DETAIL.ADVANCED.BRANDING_TITLE' | translate"
            [subtitle]="'DESIGN.THEME_DETAIL.ADVANCED.BRANDING_DESCRIPTION' | translate"
        >
            <ng-container actions>
                <tec-link
                    [label]="'DESIGN.THEME_DETAIL.ADVANCED.RESET' | translate"
                    (click)="resetAdvancedBranding()"
                />
            </ng-container>
            <tec-item
                [tagImage]="{ icon: 'icon-check-circle' }"
                [title]="'DESIGN.THEME_DETAIL.ADVANCED.WATERMARK' | translate"
                [outline]="true"
            >
                <tec-info-group>
                    <span>{{ 'DESIGN.THEME_DETAIL.ADVANCED.WATERMARK_DESCRIPTION' | translate }}</span>
                </tec-info-group>
                <tec-switch size="sm" formControlName="watermark" />
            </tec-item>
            <tec-divider />
            <tec-item
                [tagImage]="{ icon: 'icon-text' }"
                [title]="'DESIGN.THEME_DETAIL.ADVANCED.PRODUCT_NAME' | translate"
                [outline]="true"
            >
                <tec-info-group>
                    <span>{{ 'DESIGN.THEME_DETAIL.ADVANCED.PRODUCT_NAME_DESCRIPTION' | translate }}</span>
                </tec-info-group>
                <tec-switch size="sm" formControlName="show_product_name" />
            </tec-item>
        </tec-tile>
    </ng-template>
</form>
}
